/**
 * iOS-compatible fetch wrapper with timeout and retry logic
 * Fixes "Fetch is aborted" errors on iPhone/iPad
 * Includes request deduplication and caching to prevent flickering
 */

import requestCache from './requestCache';

// Detect iOS device
const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

// iOS-friendly timeout
const REQUEST_TIMEOUT = isIOS() ? 45000 : 30000; // 45s for iOS, 30s for others
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;

/**
 * Fetch with timeout using AbortController
 * @param {string} url - The URL to fetch
 * @param {object} options - Fetch options
 * @param {number} timeout - Timeout in milliseconds
 * @returns {Promise<Response>}
 */
const fetchWithTimeout = (url, options = {}, timeout = REQUEST_TIMEOUT) => {
  const controller = new AbortController();
  const { signal, ...restOptions } = options;
  
  // Set timeout
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  
  return fetch(url, {
    ...restOptions,
    signal: controller.signal,
    // iOS-specific: Ensure credentials are handled properly
    credentials: options.credentials || 'same-origin',
    // iOS-specific: Add cache control
    cache: options.cache || 'no-cache',
  })
    .finally(() => {
      clearTimeout(timeoutId);
    });
};

/**
 * Fetch with retry logic for iOS network issues
 * @param {string} url - The URL to fetch
 * @param {object} options - Fetch options
 * @param {number} retries - Number of retries
 * @returns {Promise<Response>}
 */
const fetchWithRetry = async (url, options = {}, retries = MAX_RETRIES) => {
  let lastError;
  
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      // Add iOS-specific headers
      const enhancedOptions = {
        ...options,
        headers: {
          ...options.headers,
          ...(isIOS() ? {
            'X-Requested-With': 'XMLHttpRequest',
            'Cache-Control': 'no-cache',
          } : {}),
        },
      };
      
      const response = await fetchWithTimeout(url, enhancedOptions);
      
      // If response is ok or client error (4xx), don't retry
      if (response.ok || (response.status >= 400 && response.status < 500)) {
        return response;
      }
      
      // Server error (5xx) - retry
      throw new Error(`Server error: ${response.status}`);
      
    } catch (error) {
      lastError = error;
      
      // Check if error is retryable
      const isAbortError = error.name === 'AbortError' || error.message.includes('abort');
      const isNetworkError = error.message.includes('network') || error.message.includes('Failed to fetch');
      const isRetryable = isAbortError || isNetworkError;
      
      // Don't retry if not retryable or last attempt
      if (!isRetryable || attempt === retries) {
        if (isIOS()) {
          console.error(`[iOS Fetch] Request failed after ${attempt} attempts:`, {
            url,
            error: error.message,
            isAbortError,
            isNetworkError
          });
        }
        throw error;
      }
      
      // Calculate delay with exponential backoff and jitter
      const delay = RETRY_DELAY * Math.pow(2, attempt) + Math.random() * 1000;
      
      if (isIOS()) {
        console.log(`[iOS Fetch] Retry attempt ${attempt + 1}/${retries} after ${delay}ms`);
      }
      
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError;
};

/**
 * Main export - iOS-compatible fetch with timeout, retry, and caching
 * Drop-in replacement for native fetch()
 * 
 * @param {string} url - The URL to fetch
 * @param {object} options - Fetch options
 * @param {boolean} options.skipCache - Skip cache and make fresh request
 * @param {boolean} options.silent - Silent request (use cached data, update in background)
 * @returns {Promise<Response>}
 * 
 * Usage:
 *   import iosFetch from './utils/iosFetch';
 *   const response = await iosFetch(url, options);
 */
export default async function iosFetch(url, options = {}) {
  const { skipCache = false, silent = false, ...fetchOptions } = options;

  // For POST/PUT/DELETE requests, skip cache
  const method = fetchOptions.method?.toUpperCase() || 'GET';
  const shouldCache = method === 'GET' && !skipCache;

  // Use cached fetch for GET requests (prevents duplicate requests and flickering)
  if (shouldCache) {
    try {
      const fetchFn = async (url, opts) => {
        if (isIOS() || opts.forceRetry) {
          return fetchWithRetry(url, opts);
        }
        return fetchWithTimeout(url, opts);
      };

      // Use request cache for deduplication and stale-while-revalidate
      const data = await requestCache.fetch(url, fetchOptions, fetchFn);
      
      // Return response-like object with safe json/clone methods
      return {
        ok: true,
        status: 200,
        json: async () => data,
        text: async () => JSON.stringify(data),
        clone: function() { 
          return { 
            json: async () => data,
            text: async () => JSON.stringify(data),
            ok: true,
            status: 200
          };
        }
      };
    } catch (error) {
      // Fallback to direct fetch on error
      console.error('[iosFetch] Cache error, falling back to direct fetch:', error);
    }
  }

  // For non-GET requests or cache skip, use direct fetch
  if (isIOS() || fetchOptions.forceRetry) {
    return fetchWithRetry(url, fetchOptions);
  }
  
  return fetchWithTimeout(url, fetchOptions);
}

/**
 * Helper to create iOS-compatible fetch with authentication
 * @param {string} url - The URL to fetch
 * @param {object} options - Fetch options (will merge with auth headers)
 * @returns {Promise<Response>}
 */
export async function iosFetchWithAuth(url, options = {}) {
  const token = localStorage.getItem('token');
  
  const authOptions = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  };
  
  return iosFetch(url, authOptions);
}

/**
 * Helper for POST requests with JSON body
 * @param {string} url - The URL to post to
 * @param {object} data - Data to send as JSON
 * @param {object} options - Additional fetch options
 * @returns {Promise<Response>}
 */
export async function iosPost(url, data, options = {}) {
  return iosFetchWithAuth(url, {
    method: 'POST',
    body: JSON.stringify(data),
    ...options,
  });
}

/**
 * Helper for GET requests with authentication
 * @param {string} url - The URL to get
 * @param {object} options - Additional fetch options
 * @returns {Promise<Response>}
 */
export async function iosGet(url, options = {}) {
  return iosFetchWithAuth(url, {
    method: 'GET',
    ...options,
  });
}

/**
 * Helper for PUT requests with JSON body
 * @param {string} url - The URL to put to
 * @param {object} data - Data to send as JSON
 * @param {object} options - Additional fetch options
 * @returns {Promise<Response>}
 */
export async function iosPut(url, data, options = {}) {
  return iosFetchWithAuth(url, {
    method: 'PUT',
    body: JSON.stringify(data),
    ...options,
  });
}

// Export isIOS helper for conditional logic
export { isIOS };
